// ** React Imports
import { useEffect, useMemo, useState, memo } from "react";
import { useLocation } from "react-router-dom";

// ** Redux & Store & Actions
import { useSelector, useDispatch } from "react-redux";
import { getUser } from "store/modules/global-user/actions/get-user.action";
import {
	setIsClient,
	setIsAuth,
	setAuthModalVersion,
	setScriptsLoaded,
} from "store/modules/global-common.slice";

// ** Third Party Components
import { Helmet } from "react-helmet";
import { window } from "ssr-window";
import { ThemeProvider } from "styled-components";
import cookie from "js-cookie";
import { useTranslation } from "react-i18next";
import { ToastContainer } from "react-toastify";

// ** Contexts
import { DeviceTypeProvider } from "contexts/deviceTypeContext";

// ** Config
import config from "configuration";

// ** Custom Hooks
import useBreakpoint from "hooks/useBreakpoint";
import useMobileDetect from "hooks/useMobileDetectHook";
import useDarkMode from "hooks/useDarkMode";
import usePWAInstalable from "hooks/usePWAInstalable";
import useWindowScrollbarWidth from "hooks/useWindowScrollbarWidth";
import { useConsentService } from "hooks/useConsentService";

// ** Utils & Helpers
import { convertToMilion, floor } from "utils/math";
import api from "helpers/ApiClient";
import getTokenInfo from "helpers/getTokenInfo";

// ** Styles Import
import darkTheme from "styles/darkTheme";
import lightTheme from "styles/lightTheme";
import landingTheme from "styles/landingTheme";
import { StyledAppWrapper } from "./styles/AppWrapper.styled";
import ScriptLoader from "components/global/ScriptLoader/ScriptLoader";

// #####################################################

const arePropsEqual = (prevProps, nextProps) => {
	if (!prevProps || !nextProps) return false;

	return prevProps?.location?.pathname === nextProps?.location?.pathname;
};

// #####################################################
const WrapperComponent = memo(({ children }) => {
	const { initializeConsent } = useConsentService();

	const location = useLocation();

	const dispatch = useDispatch();

	const { t } = useTranslation(["common"]);

	const cookieAuth = cookie.get(config.auth.cookieName);

	const language = useSelector((state) => state.globalCommon.language);
	const userLoaded = useSelector((state) => state.globalUser.loaded);

	const isAuth = useSelector((state) => state.globalCommon.isAuth);

	const hasAnalyticsStorageConsent = useSelector(
		(state) => state.globalCommon.cookieConsent.settings.analytics_storage
	);
	const hasAdsPersonalizationStorageConsent = useSelector(
		(state) => state.globalCommon.cookieConsent.settings.ad_personalization
	);

	// ** Tryb Ciemny
	const { isDarkMode } = useDarkMode();

	// ** check PWA is Installable
	usePWAInstalable();

	// **
	const isLanding = location.pathname === "/";

	// ** userAgent Pobrany z Requestu Użytkownika Przez Serwer
	const userAgentFromServerState = useSelector(
		(state) => state.globalCommon.userAgent.ua
	);

	// ** Hook useMobileDetect
	const mobileDetect = useMobileDetect(userAgentFromServerState);
	const verticalScrollbarWidth = useWindowScrollbarWidth();

	const isDesktopFromServer = mobileDetect.isDesktop();
	const isMobileFromServer = mobileDetect.isMobile();
	const isDesktopFromClient = useBreakpoint("desktop", "min");
	const isGiantFromClient = useBreakpoint("giant", "min");
	const isTabletFromClient = useBreakpoint("desktop", "max");
	const isPhoneFromClient = useBreakpoint("phone", "max");
	const [prevCookieAuth, setPrevCookieAuth] = useState(cookieAuth || null);

	const [deviceType, setDeviceType] = useState({
		isGiant: isDesktopFromServer,
		isDesktop: isDesktopFromServer,
		isTablet: isMobileFromServer,
		isPhone: isMobileFromServer,
	});

	const isClient = useSelector((state) => state.globalCommon.isClient);

	// ** Statystyki - Liczba użytkowników
	const usersTotalCount = useSelector(
		(state) => state.globalCommon.statistics.usersTotal
	);

	useEffect(() => {
		initializeConsent();
	}, []);

	useEffect(() => {
		setDeviceType({
			isGiant: isGiantFromClient,
			isDesktop: isDesktopFromClient,
			isTablet: isTabletFromClient,
			isPhone: isPhoneFromClient,
		});
	}, [
		isGiantFromClient,
		isDesktopFromClient,
		isTabletFromClient,
		isPhoneFromClient,
	]);

	useEffect(() => {
		if (!isClient) {
			dispatch(setIsClient(true));
		}
	}, [isAuth]);

	useEffect(() => {
		const currentApiLanguage = api.getLanguage();
		if (currentApiLanguage !== language) {
			api.modifyLanguageHeader(language);
		}
	}, [language]);

	useEffect(() => {
		if (isAuth && !userLoaded) {
			dispatch(getUser());
		}

		// ** Dodanie zdarzenia niestandardowego - 'openInPWA' w przypadku gdy strona została otwara w aplikacji PWA
		const mqStandAlone = "(display-mode: standalone)";
		if (
			navigator.standalone ||
			(window?.matchMedia && window.matchMedia(mqStandAlone).matches)
		) {
			if (
				window?.dataLayer &&
				typeof window.dataLayer.push === "function"
			) {
				window.dataLayer.push({
					event: "openInPWA",
				});
			}
		}
	}, [isAuth]);

	// ** Przelogowanie użytkownika po zalogowaniu się na inne konto w nowej karcie
	// ** (sposób wywołania - otwieramy dwa razy landing w kartach, dopiero później logujemy się w jednej i drugiej karcie na różne konta)
	useEffect(() => {
		if (
			cookieAuth !== prevCookieAuth &&
			prevCookieAuth &&
			cookieAuth &&
			userLoaded &&
			isAuth
		) {
			const info = getTokenInfo(cookieAuth);
			const infoPrev = getTokenInfo(prevCookieAuth);

			if (info?.id !== infoPrev?.id) {
				dispatch(getUser());
			}
		} else if (!isAuth && cookieAuth) {
			dispatch(setIsAuth(true));
		} else if (!cookieAuth) {
			dispatch(setIsAuth(false));
		}
		setPrevCookieAuth(cookieAuth);
	}, [cookieAuth, prevCookieAuth, isAuth, userLoaded]);

	// **
	const memonizedTheme = useMemo(() => {
		if (isLanding && !isAuth && isDarkMode) {
			return { ...landingTheme, verticalScrollbarWidth };
		} else if (isDarkMode) {
			return { ...darkTheme, verticalScrollbarWidth };
		} else {
			return { ...lightTheme, verticalScrollbarWidth };
		}
	}, [isDarkMode, isLanding, isAuth, verticalScrollbarWidth]);

	const titleTemplate = useMemo(() => {
		const titleTemplate = t("common:titleTemplate");
		return `%s - ${titleTemplate}`;
	}, [language]);

	const convertedValue = useMemo(() => {
		return floor(convertToMilion(usersTotalCount))
			? floor(convertToMilion(usersTotalCount))
					.toFixed(1)
					.split(".")
					.join(",")
					.toLocaleString("pl-PL")
			: "1,0";
	}, [usersTotalCount]);

	const metaDescription = `Umawiaj się na sex spotkania albo znajdź partnera na dłużej! Ponad ${convertedValue} mln realnych osób na Datezone. Wejdź i sprawdź za darmo!`;

	// ** A/B Tests !!!
	// ** Przy dodawaniu wszystkich badań gdzie warunek zależy od ID użytkownika zadbać, aby dodać sprawdzenie:
	// ** "if (isAuth && userId)" - ponieważ przez jakiś czas po zalogowaniu userId === null co może wpływać na
	// ** ilość poszczególnych zdarzeń!!!

	const authModalVersion = useSelector(
		(state) => state.globalCommon.authModal.version
	);
	const hasSetAuthModalVersion = useSelector(
		(state) => state.globalCommon.authModal.hasSetVersion
	);

	const cookieAuthModalVersion = cookie.get(
		config.authModalVersion.cookieName
	);

	useEffect(() => {
		if (!hasSetAuthModalVersion) {
			if (cookieAuthModalVersion) {
				dispatch(setAuthModalVersion(cookieAuthModalVersion));
			} else {
				const version = Math.floor(Math.random() * 2);

				if (version === 0) {
					dispatch(setAuthModalVersion("onlyDark"));
					cookie.set(config.authModalVersion.cookieName, "onlyDark", {
						...config.authModalVersion.cookieOptionsExpress,
					});
				} else {
					dispatch(setAuthModalVersion("darkAndLight"));
					cookie.set(
						config.authModalVersion.cookieName,
						"darkAndLight",
						{
							...config.authModalVersion.cookieOptionsExpress,
						}
					);
				}
			}
		}
	}, [authModalVersion, hasSetAuthModalVersion]);

	useEffect(() => {
		window.gtag(
			"event",
			`load_${authModalVersion}_mode_${isDarkMode ? "dark" : "light"}`
		);
	}, [authModalVersion, isDarkMode]);

	// #####################################################

	return (
		<ThemeProvider theme={memonizedTheme}>
			<DeviceTypeProvider value={deviceType}>
				<StyledAppWrapper
					id="app"
					className={isDarkMode ? "isDarkMode" : "isLightMode"}
				>
					<Helmet titleTemplate={titleTemplate}>
						<meta name="description" content={metaDescription} />
						{(hasAnalyticsStorageConsent ||
							hasAdsPersonalizationStorageConsent) && (
							<meta
								httpEquiv="Delegate-CH"
								content="sec-ch-ua https://ads.trafficjunky.net; sec-ch-ua-arch https://ads.trafficjunky.net; sec-ch-ua-full-version-list https://ads.trafficjunky.net; sec-ch-ua-mobile https://ads.trafficjunky.net; sec-ch-ua-model https://ads.trafficjunky.net; sec-ch-ua-platform https://ads.trafficjunky.net; sec-ch-ua-platform-version https://ads.trafficjunky.net;"
							/>
						)}
						<link
							rel="icon"
							type="image/png"
							sizes="16x16"
							href="/ico/16x16.png"
						/>
						<link
							rel="icon"
							type="image/png"
							sizes="32x32"
							href="/ico/32x32.png"
						/>
						<link
							rel="icon"
							type="image/png"
							sizes="192x192"
							href="/ico/192x192.png"
						/>
						<link rel="manifest" href="/datezone.webmanifest" />
					</Helmet>
					{children}
				</StyledAppWrapper>
				{hasAdsPersonalizationStorageConsent && (
					<ScriptLoader
						src="https://static.trafficjunky.com/js/mp.min.js"
						content={`var _mpevt = _mpevt || [];`}
						async
						defer
						onLoad={() => {
							window._mpevt.push([
								"www.datezone.com._event",
								"TJ",
								"frqwjiun6134dsf78",
								"525600",
							]);
							dispatch(
								setScriptsLoaded({ name: "tj", isLoaded: true })
							);
						}}
					/>
				)}
				<ToastContainer autoClose={10000} />
			</DeviceTypeProvider>
		</ThemeProvider>
	);
}, arePropsEqual);

// #####################################################

export default WrapperComponent;

// #####################################################
